'use client';

import { useState } from 'react';
import { flushSync } from 'react-dom';
import { Checkbox, TextField, Divider, Loader } from 'ui';
import { useTranslation } from '@/i18n/client';
import { useProductList } from '@/modules/product/context/productList';
import { useCurrentLocale } from '@/shared/utils/client';
import { fetchBrandsFilterList } from '@/modules/brands/api';
import { fetchFilterList } from '@/modules/product/api/productList';
import { formatFiltersBeforeSend } from '../filterBar/utils';

import styles from './ColorFilter.module.scss';
import { ColorFilterProps } from './types';

export const ColorFilter = ({
	initialValues,
	filteredValues,
	slug,
	sidebarFilters,
	selectedValues,
	setSidebarFilters,
	setSidebarFilteredFilterList,
}: ColorFilterProps) => {
	const { t } = useTranslation(['common']);
	const locale = useCurrentLocale();
	const { brandSlug, categorySlug } = useProductList();
	const [search, setSearch] = useState('');
	const [filteredColors, setFilteredColors] = useState(initialValues);
	const [isLoading, setIsLoading] = useState(false);

	const handleSearchChange = (value: string) => {
		setFilteredColors(
			initialValues.filter((v) =>
				v.value.toLocaleLowerCase(locale).trim().includes(value.toLocaleLowerCase(locale).trim())
			)
		);
		setSearch(value);
	};

	const handleSelectValue = async (selected: boolean, name: string) => {
		const newSidebarFiltersValue = [...selectedValues] as string[];

		if (!selected) {
			if (newSidebarFiltersValue.indexOf(name) > -1) {
				return;
			}

			newSidebarFiltersValue.push(name);
		} else {
			const valueIndex = newSidebarFiltersValue.indexOf(name);

			if (newSidebarFiltersValue.indexOf(name) < 0) {
				return;
			}

			newSidebarFiltersValue.splice(valueIndex, 1);
		}

		flushSync(() => {
			setSidebarFilters((oldValue) => {
				const newValue = [...oldValue];

				const filterIndex = newValue.findIndex((filter) => filter.slug === slug);

				newValue[filterIndex].selectedValue = newSidebarFiltersValue;

				return newValue;
			});
		});
		setIsLoading(true);

		const newFilters = formatFiltersBeforeSend([...sidebarFilters]);

		if (newSidebarFiltersValue.length > 0) {
			newFilters[slug] = newSidebarFiltersValue;
		} else {
			delete newFilters[slug];
		}

		const response = brandSlug
			? await fetchBrandsFilterList(brandSlug, newFilters)
			: await fetchFilterList(categorySlug, newFilters);

		setSidebarFilteredFilterList(response);
		setIsLoading(false);
	};

	return (
		<>
			<div className={styles.colorFilter}>
				{initialValues.length >= 10 && (
					<>
						<TextField
							label={t('common.search')}
							value={search}
							onChange={(e) => handleSearchChange(e.target.value)}
						/>
						<Divider disableBackground bottom={15} />
					</>
				)}
				{filteredColors
					.sort((a, b) => (a.value > b.value ? 1 : b.value > a.value ? -1 : 0))
					.map((color, index) => {
						const disabled =
							filteredValues.findIndex(
								(filteredValue) =>
									filteredValue.value === color.value && filteredValue.hex === color.hex
							) < 0;

						if (disabled) {
							return;
						}

						return (
							<Checkbox
								key={index}
								childrenClassName={styles.colorFilter__colorWrapper}
								labelClassName={styles.colorFilter__label}
								position="start"
								checked={selectedValues.findIndex((value) => value === color.value) > -1}
								onChange={() =>
									handleSelectValue(
										selectedValues.findIndex((value) => value === color.value) > -1,
										color.value
									)
								}
								disabled={disabled}
							>
								<span
									className={styles['colorFilter--color']}
									style={{ backgroundColor: color.hex }}
								/>
								{color.value}
							</Checkbox>
						);
					})}
				{filteredColors
					.sort((a, b) => (a.value > b.value ? 1 : b.value > a.value ? -1 : 0))
					.map((color, index) => {
						const disabled =
							filteredValues.findIndex(
								(filteredValue) =>
									filteredValue.value === color.value && filteredValue.hex === color.hex
							) < 0;

						if (!disabled) {
							return;
						}

						return (
							<Checkbox
								key={index}
								childrenClassName={styles.colorFilter__colorWrapper}
								labelClassName={styles.colorFilter__label}
								position="start"
								checked={selectedValues.findIndex((value) => value === color.value) > -1}
								onChange={() =>
									handleSelectValue(
										selectedValues.findIndex((value) => value === color.value) > -1,
										color.value
									)
								}
								disabled={disabled}
							>
								<span
									className={styles['colorFilter--color']}
									style={{ backgroundColor: color.hex }}
								/>
								{color.value}
							</Checkbox>
						);
					})}
			</div>
			{isLoading && <Loader />}
		</>
	);
};
